import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDisplayTime, getZonedDateTimeParts, isSameZonedCalendarDay } from '../../common/ecxTimeUtils';
import { parseAppointmentTimeToMinutes } from './subparts/appointmentsQuery';
import AppointmentCalendarBlock from './AppointmentCalendarBlock';

const SLOT_HEIGHT_PX = 56;
const DEFAULT_BASE_SLOT_MINUTES = 30;
const MIN_SLOT_MINUTES = 15;
const MAX_SLOT_MINUTES = 60;
const DEFAULT_START_MINUTES = 8 * 60;
const DEFAULT_END_MINUTES = 18 * 60;
const MIN_VISIBLE_DURATION = MIN_SLOT_MINUTES;
const BLOCK_GAP_PX = 2;
const HOUR_MINUTES = 60;
const HALF_HOUR_MINUTES = 30;
const MIN_DOCTOR_COLUMN_WIDTH = 14;
const DOCTOR_ORDER_STORAGE_KEY = 'ecx.appointments.calendarDoctorOrder';
const WEEKDAY_KEYS_BY_JS_DAY = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
];

const isClinicHourOpen = (hour) => hour?.status === true || hour?.status === 'true';

const getBlockDensity = (heightPx) => {
    if (heightPx < 40) {
        return 'micro';
    }

    if (heightPx < 78) {
        return 'compact';
    }

    return 'full';
};

/** Snap the day's shortest appointment into a scale unit (15 / 30 / 60). */
const resolveBaseSlotMinutes = (minDurationMinutes) => {
    if (!Number.isFinite(minDurationMinutes) || minDurationMinutes <= 0) {
        return DEFAULT_BASE_SLOT_MINUTES;
    }

    if (minDurationMinutes <= MIN_SLOT_MINUTES) {
        return MIN_SLOT_MINUTES;
    }

    if (minDurationMinutes <= HALF_HOUR_MINUTES) {
        return HALF_HOUR_MINUTES;
    }

    return MAX_SLOT_MINUTES;
};

const detectBaseSlotMinutes = (appointments) => {
    let minDuration = Number.POSITIVE_INFINITY;

    (appointments || []).forEach((appointment) => {
        const start = parseAppointmentTimeToMinutes(appointment?.startTime);
        const end = parseAppointmentTimeToMinutes(appointment?.endTime);

        if (
            start >= Number.MAX_SAFE_INTEGER ||
            end >= Number.MAX_SAFE_INTEGER ||
            end <= start
        ) {
            return;
        }

        minDuration = Math.min(minDuration, end - start);
    });

    return resolveBaseSlotMinutes(minDuration);
};

const resolveSelectedDate = (selectedDate) => {
    if (selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())) {
        return selectedDate;
    }

    if (selectedDate) {
        const parsed = new Date(selectedDate);

        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }
    }

    return null;
};

const getClinicWindowForDate = (clinicHours, selectedDate) => {
    const viewing = resolveSelectedDate(selectedDate);

    if (!viewing || !Array.isArray(clinicHours) || clinicHours.length === 0) {
        return null;
    }

    const weekdayKey = WEEKDAY_KEYS_BY_JS_DAY[viewing.getDay()];
    const dayHours = clinicHours.find(
        (hour) => String(hour?.day || '').trim().toUpperCase() === weekdayKey
    );

    if (!dayHours || !isClinicHourOpen(dayHours)) {
        return null;
    }

    const start = parseAppointmentTimeToMinutes(dayHours.start);
    const end = parseAppointmentTimeToMinutes(dayHours.end);

    if (
        start >= Number.MAX_SAFE_INTEGER ||
        end >= Number.MAX_SAFE_INTEGER ||
        end <= start
    ) {
        return null;
    }

    return { start, end };
};

const readStoredDoctorOrder = () => {
    try {
        const stored = window.localStorage.getItem(DOCTOR_ORDER_STORAGE_KEY);

        if (!stored) {
            return [];
        }

        const parsed = JSON.parse(stored);

        return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
        return [];
    }
};

const writeStoredDoctorOrder = (order) => {
    try {
        window.localStorage.setItem(DOCTOR_ORDER_STORAGE_KEY, JSON.stringify(order));
    } catch {
        // Ignore storage failures (private mode / quota).
    }
};

const reorderKeys = (keys, fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) {
        return keys;
    }

    const next = [...keys];
    const fromIndex = next.indexOf(fromKey);
    const toIndex = next.indexOf(toKey);

    if (fromIndex === -1 || toIndex === -1) {
        return keys;
    }

    next.splice(fromIndex, 1);
    next.splice(toIndex, 0, fromKey);

    return next;
};

const APPOINTMENT_VIEW_OPTIONS = [
    { value: 'list', label: 'List', icon: 'bi-list-ul' },
    { value: 'calendar', label: 'Calendar', icon: 'bi-calendar3' },
];

export { APPOINTMENT_VIEW_OPTIONS };

const roundDownToHour = (minutes) => Math.floor(minutes / HOUR_MINUTES) * HOUR_MINUTES;
const roundUpToHour = (minutes) => Math.ceil(minutes / HOUR_MINUTES) * HOUR_MINUTES;
const clampMinutes = (minutes) => Math.min(24 * 60, Math.max(0, minutes));

const getDoctorKey = (appointment) => {
    if (appointment?.userId) {
        return String(appointment.userId);
    }

    const doctor = appointment?.doctor || {};
    const nameKey = [doctor.firstName, doctor.lastName]
        .filter(Boolean)
        .join(' ')
        .trim()
        .toLowerCase();

    return nameKey || 'unassigned';
};

const getDoctorDisplayName = (appointment) => {
    const doctor = appointment?.doctor || {};
    const name = [doctor.firstName, doctor.lastName].filter(Boolean).join(' ').trim();

    return name ? `Dr. ${name}` : 'Unassigned';
};

const getAppointmentBounds = (appointment) => {
    const start = parseAppointmentTimeToMinutes(appointment.startTime);
    let end = parseAppointmentTimeToMinutes(appointment.endTime);

    if (start >= Number.MAX_SAFE_INTEGER) {
        return null;
    }

    if (end >= Number.MAX_SAFE_INTEGER || end <= start) {
        end = start + MIN_VISIBLE_DURATION;
    }

    return {
        start,
        end: Math.max(end, start + MIN_VISIBLE_DURATION),
    };
};

const getDayRange = (appointments, clinicHours, selectedDate) => {
    const clinicWindow = getClinicWindowForDate(clinicHours, selectedDate);
    let start = clinicWindow
        ? clampMinutes(roundDownToHour(clinicWindow.start) - HOUR_MINUTES)
        : DEFAULT_START_MINUTES;
    let end = clinicWindow
        ? clampMinutes(roundUpToHour(clinicWindow.end) + HOUR_MINUTES)
        : DEFAULT_END_MINUTES;
    let hasTimedAppointments = false;
    let earliestAppointment = null;
    let latestAppointment = null;

    (appointments || []).forEach((appointment) => {
        const bounds = getAppointmentBounds(appointment);

        if (!bounds) {
            return;
        }

        hasTimedAppointments = true;
        earliestAppointment =
            earliestAppointment === null ? bounds.start : Math.min(earliestAppointment, bounds.start);
        latestAppointment =
            latestAppointment === null ? bounds.end : Math.max(latestAppointment, bounds.end);
    });

    if (!clinicWindow && hasTimedAppointments) {
        start = clampMinutes(roundDownToHour(earliestAppointment) - HOUR_MINUTES);
        end = clampMinutes(roundUpToHour(latestAppointment) + HOUR_MINUTES);
    } else if (hasTimedAppointments) {
        start = Math.min(start, clampMinutes(roundDownToHour(earliestAppointment)));
        end = Math.max(end, clampMinutes(roundUpToHour(latestAppointment)));
    }

    if (end <= start) {
        end = clampMinutes(start + HOUR_MINUTES);
    }

    return { start, end };
};

const buildHourMarks = (rangeStart, rangeEnd) => {
    const marks = [];

    for (let minute = rangeStart; minute <= rangeEnd; minute += HOUR_MINUTES) {
        marks.push(minute);
    }

    return marks;
};

const buildSlotMarks = (rangeStart, rangeEnd, slotMinutes) => {
    const step = Math.max(slotMinutes, MIN_SLOT_MINUTES);
    const marks = [];

    for (let minute = rangeStart + step; minute < rangeEnd; minute += step) {
        if (minute % HOUR_MINUTES === 0) {
            continue;
        }

        marks.push(minute);
    }

    return marks;
};

const layoutOverlappingInColumn = (appointments) => {
    const items = appointments
        .map((appointment) => {
            const bounds = getAppointmentBounds(appointment);

            if (!bounds) {
                return null;
            }

            return {
                appointment,
                start: bounds.start,
                end: bounds.end,
            };
        })
        .filter(Boolean)
        .sort((left, right) => {
            if (left.start !== right.start) {
                return left.start - right.start;
            }

            return right.end - left.end;
        });

    const columnEnds = [];
    const positioned = items.map((item) => {
        let column = columnEnds.findIndex((end) => end <= item.start);

        if (column === -1) {
            column = columnEnds.length;
            columnEnds.push(item.end);
        } else {
            columnEnds[column] = item.end;
        }

        return {
            ...item,
            column,
        };
    });

    const clusters = [];

    positioned.forEach((item) => {
        const overlappingCluster = clusters.find((cluster) =>
            cluster.items.some((member) => item.start < member.end && item.end > member.start)
        );

        if (overlappingCluster) {
            overlappingCluster.items.push(item);
            overlappingCluster.columnCount = Math.max(overlappingCluster.columnCount, item.column + 1);
            return;
        }

        clusters.push({
            items: [item],
            columnCount: item.column + 1,
        });
    });

    return clusters.flatMap((cluster) =>
        cluster.items.map((item) => ({
            ...item,
            columnCount: cluster.columnCount,
        }))
    );
};

const buildDoctorColumns = (appointments, doctorOrder = []) => {
    const columnsByKey = new Map();
    const orderIndex = new Map(doctorOrder.map((key, index) => [key, index]));

    appointments.forEach((appointment) => {
        const key = getDoctorKey(appointment);

        if (!columnsByKey.has(key)) {
            columnsByKey.set(key, {
                key,
                name: getDoctorDisplayName(appointment),
                appointments: [],
            });
        }

        columnsByKey.get(key).appointments.push(appointment);
    });

    return Array.from(columnsByKey.values())
        .map((column) => ({
            ...column,
            events: layoutOverlappingInColumn(column.appointments),
            count: column.appointments.length,
        }))
        .sort((left, right) => {
            const leftOrder = orderIndex.has(left.key) ? orderIndex.get(left.key) : Number.MAX_SAFE_INTEGER;
            const rightOrder = orderIndex.has(right.key) ? orderIndex.get(right.key) : Number.MAX_SAFE_INTEGER;

            if (leftOrder !== rightOrder) {
                return leftOrder - rightOrder;
            }

            return left.name.localeCompare(right.name);
        });
};

const formatHourLabel = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;

    return formatDisplayTime(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
};

function AppointmentDayCalendar({
    appointments,
    selectedDate,
    clinicHours = [],
    clinicZoneId = '',
    nowTick = 0,
    checkedItems,
    onItemCheckChange,
    onStatusUpdated,
}) {
    const scrollRef = useRef(null);
    const hasScrolledRef = useRef(false);
    const draggingKeyRef = useRef(null);
    const [doctorOrder, setDoctorOrder] = useState(readStoredDoctorOrder);
    const [draggingKey, setDraggingKey] = useState(null);
    const [dragOverKey, setDragOverKey] = useState(null);

    const range = useMemo(
        () => getDayRange(appointments, clinicHours, selectedDate),
        [appointments, clinicHours, selectedDate]
    );
    const selectedDateKey = useMemo(() => {
        const viewing = resolveSelectedDate(selectedDate);

        if (!viewing) {
            return '';
        }

        const year = viewing.getFullYear();
        const month = String(viewing.getMonth() + 1).padStart(2, '0');
        const day = String(viewing.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }, [selectedDate]);
    const baseSlotMinutes = useMemo(() => detectBaseSlotMinutes(appointments), [appointments]);
    const pixelsPerMinute = SLOT_HEIGHT_PX / baseSlotMinutes;
    const hourMarks = useMemo(() => buildHourMarks(range.start, range.end), [range.end, range.start]);
    const slotMarks = useMemo(
        () => buildSlotMarks(range.start, range.end, baseSlotMinutes),
        [baseSlotMinutes, range.end, range.start]
    );
    const doctorColumns = useMemo(
        () => buildDoctorColumns(appointments, doctorOrder),
        [appointments, doctorOrder]
    );

    const totalMinutes = Math.max(range.end - range.start, HOUR_MINUTES);
    const gridHeight = totalMinutes * pixelsPerMinute;
    const doctorCount = Math.max(doctorColumns.length, 1);
    const canReorderColumns = doctorColumns.length > 1;

    const persistDoctorOrder = useCallback((nextOrder) => {
        setDoctorOrder(nextOrder);
        writeStoredDoctorOrder(nextOrder);
    }, []);

    const clearDragState = useCallback(() => {
        draggingKeyRef.current = null;
        setDraggingKey(null);
        setDragOverKey(null);
    }, []);

    const handleDoctorDragStart = useCallback(
        (event, key) => {
            if (!canReorderColumns) {
                event.preventDefault();
                return;
            }

            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', key);
            draggingKeyRef.current = key;
            setDraggingKey(key);
            setDragOverKey(key);
        },
        [canReorderColumns]
    );

    const handleDoctorDragOver = useCallback(
        (event, key) => {
            if (!canReorderColumns || !draggingKeyRef.current) {
                return;
            }

            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            setDragOverKey((current) => (current === key ? current : key));
        },
        [canReorderColumns]
    );

    const handleDoctorDrop = useCallback(
        (event, targetKey) => {
            event.preventDefault();

            const sourceKey = draggingKeyRef.current || event.dataTransfer.getData('text/plain');
            const currentKeys = doctorColumns.map((column) => column.key);
            const nextVisibleOrder = reorderKeys(currentKeys, sourceKey, targetKey);

            if (nextVisibleOrder !== currentKeys) {
                const visibleSet = new Set(currentKeys);
                const preserved = doctorOrder.filter((key) => !visibleSet.has(key));
                persistDoctorOrder([...nextVisibleOrder, ...preserved]);
            }

            clearDragState();
        },
        [clearDragState, doctorColumns, doctorOrder, persistDoctorOrder]
    );

    const handleDoctorDragEnd = useCallback(() => {
        clearDragState();
    }, [clearDragState]);

    const nowIndicator = useMemo(() => {
        const viewing = resolveSelectedDate(selectedDate);

        if (!viewing || !isSameZonedCalendarDay(viewing, clinicZoneId)) {
            return null;
        }

        const zonedNow = getZonedDateTimeParts(clinicZoneId);
        const nowMinutes = zonedNow.totalMinutes;

        if (nowMinutes < range.start || nowMinutes > range.end) {
            return null;
        }

        return {
            top: (nowMinutes - range.start) * pixelsPerMinute,
            label: formatHourLabel(nowMinutes),
        };
    }, [clinicZoneId, nowTick, pixelsPerMinute, range.end, range.start, selectedDateKey]);

    const firstEventTop = useMemo(() => {
        let earliest = null;

        doctorColumns.forEach((column) => {
            column.events.forEach((event) => {
                if (earliest === null || event.start < earliest) {
                    earliest = event.start;
                }
            });
        });

        return earliest;
    }, [doctorColumns]);

    useEffect(() => {
        hasScrolledRef.current = false;
    }, [baseSlotMinutes, selectedDateKey]);

    useEffect(() => {
        if (!scrollRef.current || hasScrolledRef.current) {
            return;
        }

        const targetTop = nowIndicator
            ? Math.max(0, nowIndicator.top - 80)
            : firstEventTop !== null
              ? Math.max(0, (firstEventTop - range.start) * pixelsPerMinute - 40)
              : 0;

        scrollRef.current.scrollTop = targetTop;
        hasScrolledRef.current = true;
    }, [baseSlotMinutes, firstEventTop, nowIndicator?.top, pixelsPerMinute, range.start, selectedDateKey]);

    return (
        <div className="appointments-day-calendar-wrap">
            <div className="appointments-day-calendar" ref={scrollRef}>
                <div
                    className="appointments-day-calendar__board"
                    style={{
                        '--doctor-count': doctorCount,
                        '--doctor-col-min': `${MIN_DOCTOR_COLUMN_WIDTH}rem`,
                    }}
                >
                    <div className="appointments-day-calendar__doctor-headers">
                        <div className="appointments-day-calendar__doctor-headers-gutter" />
                        <div className="appointments-day-calendar__doctor-headers-row">
                            {doctorColumns.map((column) => {
                                const isDragging = draggingKey === column.key;
                                const isDropTarget =
                                    Boolean(draggingKey) &&
                                    dragOverKey === column.key &&
                                    draggingKey !== column.key;

                                return (
                                    <div
                                        key={column.key}
                                        className={`appointments-day-calendar__doctor-header${
                                            canReorderColumns ? ' is-draggable' : ''
                                        }${isDragging ? ' is-dragging' : ''}${
                                            isDropTarget ? ' is-drop-target' : ''
                                        }`}
                                        draggable={canReorderColumns}
                                        onDragStart={(event) => handleDoctorDragStart(event, column.key)}
                                        onDragOver={(event) => handleDoctorDragOver(event, column.key)}
                                        onDrop={(event) => handleDoctorDrop(event, column.key)}
                                        onDragEnd={handleDoctorDragEnd}
                                        title={
                                            canReorderColumns
                                                ? 'Drag to rearrange columns'
                                                : undefined
                                        }
                                    >
                                        {canReorderColumns && (
                                            <span
                                                className="appointments-day-calendar__doctor-header-grip"
                                                aria-hidden="true"
                                            >
                                                <i className="bi bi-grip-vertical" />
                                            </span>
                                        )}
                                        <span className="appointments-day-calendar__doctor-header-name">
                                            {column.name}
                                        </span>
                                        <span className="appointments-day-calendar__doctor-header-count">
                                            {column.count}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="appointments-day-calendar__body" style={{ height: `${gridHeight}px` }}>
                        <div className="appointments-day-calendar__times" aria-hidden="true">
                            {hourMarks.map((minute) => (
                                <div
                                    key={minute}
                                    className="appointments-day-calendar__time-label"
                                    style={{ top: `${(minute - range.start) * pixelsPerMinute}px` }}
                                >
                                    {formatHourLabel(minute)}
                                </div>
                            ))}
                        </div>

                        <div className="appointments-day-calendar__columns">
                            {nowIndicator && (
                                <div
                                    className="appointments-day-calendar__now"
                                    style={{ top: `${nowIndicator.top}px` }}
                                    title={`Current time · ${nowIndicator.label}`}
                                >
                                    <span className="appointments-day-calendar__now-dot" />
                                    <span className="appointments-day-calendar__now-line" />
                                </div>
                            )}

                            {doctorColumns.map((column) => (
                                <div key={column.key} className="appointments-day-calendar__column">
                                    {hourMarks.map((minute) => (
                                        <div
                                            key={`${column.key}-line-${minute}`}
                                            className="appointments-day-calendar__hour-line"
                                            style={{
                                                top: `${(minute - range.start) * pixelsPerMinute}px`,
                                            }}
                                        />
                                    ))}

                                    {slotMarks.map((minute) => (
                                        <div
                                            key={`${column.key}-slot-${minute}`}
                                            className={`appointments-day-calendar__slot-line${
                                                minute % HALF_HOUR_MINUTES === 0
                                                    ? ' appointments-day-calendar__slot-line--half'
                                                    : ''
                                            }`}
                                            style={{
                                                top: `${(minute - range.start) * pixelsPerMinute}px`,
                                            }}
                                        />
                                    ))}

                                    <div className="appointments-day-calendar__events">
                                        {column.events.map((item) => {
                                            const durationMinutes = Math.max(
                                                item.end - item.start,
                                                baseSlotMinutes
                                            );
                                            const top = (item.start - range.start) * pixelsPerMinute;
                                            const height = Math.max(
                                                durationMinutes * pixelsPerMinute - BLOCK_GAP_PX,
                                                SLOT_HEIGHT_PX - BLOCK_GAP_PX
                                            );
                                            const widthPercent = 100 / item.columnCount;
                                            const leftPercent = item.column * widthPercent;
                                            const density = getBlockDensity(height);

                                            return (
                                                <AppointmentCalendarBlock
                                                    key={item.appointment.id}
                                                    appt={item.appointment}
                                                    isSelected={checkedItems.includes(item.appointment.id)}
                                                    onCheckChange={onItemCheckChange}
                                                    onStatusUpdated={onStatusUpdated}
                                                    hideDoctor
                                                    density={density}
                                                    style={{
                                                        top: `${top}px`,
                                                        height: `${height}px`,
                                                        left: `calc(${leftPercent}% + 0.15rem)`,
                                                        width: `calc(${widthPercent}% - 0.3rem)`,
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AppointmentDayCalendar;
